import React, { PureComponent } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import Dropzone from 'react-dropzone';
import { BubbleSelectBlock, Modal, Switch, Text, TextInput, Theme } from '@simplybenefits/sb-ui-library';
import { helpers } from '@simplybenefits/sb-utils';
import { getClaimCertMeta } from '../../../../actions/users.js';
import { processWithDentalAI } from '../../../../actions/adjudication.js';
import SuccessModal from './SuccessModal.js';
import ErrorModal from './ErrorModal.js';
import pdfImg from '../../../../assets/pdf.png';

class SubmitClaimModal extends PureComponent {
    state = {
        cert: '',
        patient: null,
        patientOptions: [],
        userData: null,
        documents: [],
        isProbe: false,
        isAutoFlag: false,
        payee: null,
        certError: null,
        patientError: null,
        payeeError: null,
        documentsError: null,
        showSuccess: false,
        showError: false,
        eobData: null,
        isPending: null,
        errorVerbiage: null,
        reasonsForFlagging: null,
        claimType: null
    };

    _loadMember = async () => {
        if (!this.state.cert) {
            this.setState({ certError: 'Certificate number is required.' });
            return;
        }

        const userData = await this.props.dispatch(getClaimCertMeta(this.state.cert));

        if (userData) {
            const dependents = userData.dependents.map((dep) => ({
                name: `${ dep.firstname } ${ dep.lastname } (${ dep.relationship })`,
                id: dep.dependentID
            }));

            const primary = {
                name: `${ userData.meta.firstname } ${ userData.meta.lastname }`,
                id: userData.meta.userID
            };

            const patientOptions = [ primary, ...dependents ];

            this.setState({
                userData,
                patientOptions,
                patient: patientOptions[0],
                certError: null
            });
        }
    };

    _handleKeyPress = (event) => {
        if (event.key === 'Enter' && !this.state.userData) {
            this._loadMember();
        }
    };

    _selectDocument = (files) => {
        const file = new File([ files[0] ], helpers.cleanFilename(files[0].path), { type: files[0].type });
        const reader = new FileReader();
        reader.readAsDataURL(file);

        reader.onload = () => {
            this.setState({
                documents: [ ...this.state.documents, { file, preview: reader.result }],
                documentsError: null
            });
        };
    };

    _removeDocument = (index) => {
        this.setState({
            documents: this.state.documents.filter((_, i) => i !== index)
        });
    };

    _handleSubmitReal = () => {
        this.setState({ isProbe: false, showSuccess: false }, this._handleSubmit);
    };

    _handleSubmit = async () => {
        if (!this.state.patient) {
            this.setState({ patientError: 'Please select a patient.' });
            return;
        }

        if (!this.state.payee) {
            this.setState({ payeeError: 'Please select a payee.' });
            return;
        }

        if (this.state.documents.length === 0) {
            this.setState({ documentsError: 'At least one supporting document is required.' });
            return;
        }

        const form = new FormData();
        const isDependent = [ 'Spouse', 'Common Law', 'Child' ].some((rel) => this.state.patient.name.includes(`(${ rel })`));
        const patientName = isDependent ? this.state.patient.name.split(' (')[0] : this.state.patient.name;
        const patientType = isDependent ? 'dependent' : 'primary';
        const dependentID = isDependent ? this.state.patient.id : null;

        form.append('isProbe', this.state.isProbe);
        form.append('isAutoFlag', this.state.isAutoFlag);
        form.append('cert', this.state.cert);
        form.append('patient', patientName);
        form.append('patientType', patientType);
        form.append('dependentID', dependentID);
        form.append('payee', this.state.payee);

        // Hapi requires at least one entry for array fields
        form.append('docs', '0');

        this.state.documents.forEach((doc) => {
            form.append('docs', doc.file);
        });

        const data = await this.props.dispatch(processWithDentalAI(form));

        if (data?.type?.toUpperCase() === 'SUCCESS') {
            this.setState({
                showSuccess: true,
                eobData: data,
                isPending: false,
                claimType: data.claimType
            });
        } else if (data?.type?.toUpperCase() === 'PENDING') {
            this.setState({
                showSuccess: true,
                eobData: data,
                isPending: true,
                claimType: data.claimType,
                reasonsForFlagging: data?.reasonsForFlagging
            });
        } else if (!data || !data.type || data?.type?.toUpperCase() === 'FAILED' || data?.type?.toUpperCase() === 'FAIL') {
            this.setState({
                showError: true,
                errorVerbiage: data?.errors || data?.verbiage,
                eobData: data,
                isPending: false,
                claimType: data.claimType
            });
        }
    };

    render() {
        if (this.state.showSuccess) {
            return (
                <SuccessModal
                    type={ this.state.claimType }
                    isPending={ this.state.isPending }
                    eob={ this.state.eobData.EOB }
                    reasonsForFlagging={ this.state.reasonsForFlagging }
                    id={ this.state.eobData.id }
                    verbiage={ this.state.eobData.verbiage }
                    isProbe={ this.state.isProbe }
                    onClose={ this.props.onClose }
                    onSubmitReal={ this.state.isProbe ? this._handleSubmitReal : null }
                    onReviewClaim={ this.state.isPending ?
                        () => {
                            window.location.href = `/adjudication/simplyDental/${ this.state.claimType.toLowerCase() }/null/null/${ this.state.eobData.uid }`;
                        } :
                        null
                    }
                />
            );
        }

        if (this.state.showError) {
            return (
                <ErrorModal
                    type="Claim"
                    errors={ this.state.errorVerbiage }
                    onClose={ this.props.onClose }
                />
            );
        }

        const { userData, documents, documentsError } = this.state;

        const primaryButton = userData
            ? { label: 'Submit', onClick: this._handleSubmit }
            : { label: 'Load Member', onClick: this._loadMember };

        return (
            <Modal
                small
                open={true}
                title="Submit Transaction"
                primaryButton={ primaryButton }
                onClose={ this.props.onClose }
                loader={ this.props.loader }
            >
                <div onKeyPress={ this._handleKeyPress }>
                    <TextInput
                        label="Certificate Number"
                        variant="outlined"
                        value={ this.state.cert }
                        onChange={ (e) => this.setState({ cert: e.target.value, certError: null }) }
                        required
                        fullWidth
                        error={ Boolean(this.state.certError) }
                        helperText={ this.state.certError }
                        disabled={ Boolean(userData) }
                    />
                </div>

                {
                    userData &&
                        <>
                            <TextInput
                                label="Patient Name"
                                variant="outlined"
                                value={ this.state.patient?.name || '' }
                                onChange={ (e) => {
                                    const patient = this.state.patientOptions.find((p) => p.name === e.target.value);
                                    this.setState({ patient, patientError: null });
                                }}
                                select
                                required
                                fullWidth
                                selectOptions={ this.state.patientOptions.map((p) => p.name) }
                                error={ Boolean(this.state.patientError) }
                                helperText={ this.state.patientError }
                            />

                            <Text small margin="10px 0 0 0">
                                Select whether to pay the member or pay the dentist?
                            </Text>

                            <BubbleSelectBlock
                                first={{
                                    title: 'Pay Member',
                                    selected: this.state.payee === 'member',
                                    field: 'member'
                                }}
                                second={{
                                    title: 'Pay Dentist',
                                    selected: this.state.payee === 'dentist',
                                    field: 'dentist'
                                }}
                                handleChange={ (field) => this.setState({ payee: field, payeeError: null }) }
                            />

                            {
                                this.state.payeeError &&
                                    <Text tiny color="red" margin="-5px 0 0 15px">
                                        { this.state.payeeError }
                                    </Text>
                            }

                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 16, marginBottom: 8 }}>
                                <div>
                                    <Text bold customStyle={{ fontSize: 14 }}>Probe Submission</Text>
                                    <Text customStyle={{ fontSize: 12, color: Theme.colors.mainTextGrey }}>Test transaction to preview results without submitting</Text>
                                </div>
                                <Switch
                                    checked={ this.state.isProbe }
                                    onChange={ () => this.setState({ isProbe: !this.state.isProbe }) }
                                />
                            </div>

                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 8, marginBottom: 8 }}>
                                <div>
                                    <Text bold customStyle={{ fontSize: 14 }}>Auto Flag</Text>
                                    <Text customStyle={{ fontSize: 12, color: Theme.colors.mainTextGrey }}>Automatically flag this transaction for review</Text>
                                </div>
                                <Switch
                                    checked={ this.state.isAutoFlag }
                                    onChange={ () => this.setState({ isAutoFlag: !this.state.isAutoFlag }) }
                                />
                            </div>

                            <Text bold customStyle={{ fontSize: 14, marginTop: 20, marginBottom: 10 }}>
                                Supporting Documents
                            </Text>

                            {
                                documents.length > 0 &&
                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginBottom: 14 }}>
                                        {
                                            documents.map((doc, i) => {
                                                const isPDF = doc.file.type === 'application/pdf';

                                                return (
                                                    <div
                                                        key={ i }
                                                        style={{
                                                            position: 'relative',
                                                            width: 90,
                                                            borderRadius: 6,
                                                            border: `1px solid ${ Theme.colors.lightGrey }`,
                                                            overflow: 'hidden',
                                                            backgroundColor: '#fff'
                                                        }}
                                                    >
                                                        <img
                                                            alt={ doc.file.name }
                                                            src={ isPDF ? pdfImg : doc.preview }
                                                            style={{
                                                                width: '100%',
                                                                height: 72,
                                                                objectFit: isPDF ? 'contain' : 'cover',
                                                                padding: isPDF ? 8 : 0,
                                                                backgroundColor: Theme.colors.backgroundGrey
                                                            }}
                                                        />

                                                        <div style={{ padding: '4px 6px 5px' }}>
                                                            <Text customStyle={{
                                                                fontSize: 10,
                                                                display: 'block',
                                                                overflow: 'hidden',
                                                                textOverflow: 'ellipsis',
                                                                whiteSpace: 'nowrap',
                                                                color: Theme.colors.mainTextGrey
                                                            }}>
                                                                { doc.file.name }
                                                            </Text>
                                                        </div>

                                                        <button
                                                            onClick={ () => this._removeDocument(i) }
                                                            style={{
                                                                position: 'absolute',
                                                                top: 4,
                                                                right: 4,
                                                                width: 18,
                                                                height: 18,
                                                                borderRadius: '50%',
                                                                border: 'none',
                                                                backgroundColor: 'rgba(0,0,0,0.45)',
                                                                color: '#fff',
                                                                fontSize: 12,
                                                                lineHeight: '18px',
                                                                textAlign: 'center',
                                                                cursor: 'pointer',
                                                                padding: 0
                                                            }}
                                                        >
                                                            ×
                                                        </button>
                                                    </div>
                                                );
                                            })
                                        }
                                    </div>
                            }

                            <Dropzone
                                onDrop={ (files) => this._selectDocument(files) }
                                accept="image/png, image/jpeg, image/jpg, application/pdf"
                            >
                                {({ getRootProps, getInputProps, isDragActive }) => (
                                    <div
                                        { ...getRootProps() }
                                        style={{
                                            border: `2px dashed ${ documentsError ? Theme.colors.red : (isDragActive ? Theme.colors.skyBlue : Theme.colors.lightGrey) }`,
                                            borderRadius: 8,
                                            padding: '24px 20px',
                                            textAlign: 'center',
                                            backgroundColor: isDragActive ? Theme.colors.lightBlue : Theme.colors.backgroundGrey,
                                            cursor: 'pointer',
                                            transition: 'border-color 0.2s ease, background-color 0.2s ease',
                                            marginBottom: 4
                                        }}
                                    >
                                        <input { ...getInputProps() } />

                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            width="28"
                                            height="28"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke={ Theme.colors.mainTextGrey }
                                            strokeWidth="1.5"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            style={{ marginBottom: 8 }}
                                        >
                                            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                                            <polyline points="17 8 12 3 7 8" />
                                            <line x1="12" y1="3" x2="12" y2="15" />
                                        </svg>

                                        <Text customStyle={{ display: 'block', textAlign: 'center', fontSize: 13, marginBottom: 3 }}>
                                            { isDragActive ? 'Drop files here' : 'Drag & drop files here, or click to browse' }
                                        </Text>

                                        <Text customStyle={{ display: 'block', textAlign: 'center', fontSize: 11, color: Theme.colors.mainTextGrey }}>
                                            PNG, JPG, PDF accepted
                                        </Text>
                                    </div>
                                )}
                            </Dropzone>

                            {
                                documentsError &&
                                    <Text customStyle={{ fontSize: 12, color: Theme.colors.red, marginTop: 4 }}>
                                        { documentsError }
                                    </Text>
                            }
                        </>
                }
            </Modal>
        );
    }
}

SubmitClaimModal.propTypes = {
    onClose: PropTypes.func.isRequired,
    onSubmit: PropTypes.func.isRequired
};

/**
 * @param state
 * @returns {{loader}}
 */
const mapStateToProps = (state) => ({
    loader: state.loader
});

export default connect(mapStateToProps)(SubmitClaimModal);